import { useEffect, useState } from "react";

// Hostnames där sidan får visas som iframe-inbäddning (parent-sidan).
const ALLOWED_PARENT_HOSTS = [
  "lagamobilen.se",
  "www.lagamobilen.se",
  "dev.lagamobilen.se",
  "access.lagamobilen.se",
];

// Domäner där direktåtkomst (utan iframe) alltid är tillåten.
const TRUSTED_DIRECT_HOSTS = [
  "access.lagamobilen.se",
  "www.access.lagamobilen.se",
];

// Lovable preview/editor — så vi kan jobba med projektet i Lovable.
function isLovablePreviewHost(host: string) {
  return (
    host.endsWith(".lovable.dev") ||
    host.endsWith(".lovable.app") ||
    host.endsWith(".lovableproject.com") ||
    host === "lovable.dev" ||
    host === "localhost" ||
    host === "127.0.0.1"
  );
}

function isAllowed(): boolean {
  if (typeof window === "undefined") return true;

  // 1) Tillåt alltid i Lovable preview / lokalt utvecklingsläge.
  if (isLovablePreviewHost(window.location.hostname)) return true;

  // 1b) Tillåt direktåtkomst på våra egna branded domäner.
  if (TRUSTED_DIRECT_HOSTS.includes(window.location.hostname)) return true;

  // 2) Tillåt om sidan körs som iframe från en tillåten parent.
  const inIframe = window.top !== window.self;
  if (inIframe) {
    // a) ancestorOrigins (Chrome/Safari) — mest pålitligt, opåverkad av referrer-policy.
    try {
      const ancestors = (window.location as any).ancestorOrigins as DOMStringList | undefined;
      if (ancestors && ancestors.length > 0) {
        for (let i = 0; i < ancestors.length; i++) {
          const host = new URL(ancestors[i]).hostname;
          if (ALLOWED_PARENT_HOSTS.some(h => host === h || host.endsWith("." + h))) {
            return true;
          }
        }
      }
    } catch {
      // ignore
    }
    // b) Fallback: document.referrer (Firefox m.fl.).
    try {
      if (document.referrer) {
        const ref = new URL(document.referrer);
        if (ALLOWED_PARENT_HOSTS.some(h => ref.hostname === h || ref.hostname.endsWith("." + h))) {
          return true;
        }
      }
    } catch {
      // ignore
    }
    // c) Sista fallback: om vi är i iframe utan tillgänglig parent-info (t.ex. strikt
    // referrer-policy och ingen ancestorOrigins), tillåt ändå — annars blir sidan helt tom
    // när WordPress bäddar in oss. Direktbesök utan iframe blockeras fortfarande.
    return true;
  }

  return false;
}

export function EmbedGate({ children }: { children: React.ReactNode }) {
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    setAllowed(isAllowed());
  }, []);

  if (allowed === null) return null;

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-ink)] text-white px-6">
        <div className="max-w-md text-center">
          <div className="font-display font-light tracking-[0.2em] text-[11px] uppercase text-white/60">
            Lagamobilen
          </div>
          <div className="font-display font-semibold text-[var(--color-gold)] text-2xl tracking-[0.2em] mt-1">
            ACCESS
          </div>
          <h1 className="font-display font-bold text-2xl mt-8">Sidan är inte tillgänglig här</h1>
          <p className="text-white/70 text-sm mt-3 leading-relaxed">
            Lagamobilen Access är endast tillgängligt via{" "}
            <a
              href="https://lagamobilen.se/access"
              className="text-[var(--color-gold)] underline hover:no-underline"
            >
              lagamobilen.se/access
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
